import { useState, useRef, useCallback } from 'react'
import { IconPhoto, IconX, IconStar, IconArrowLeft, IconArrowRight } from '@tabler/icons-react'

// PhotoEntry supports both new (file present) and existing (server-saved) photos
export type PhotoEntry = {
  id: string
  url: string          // object URL for new, storageKey/path for existing
  file?: File          // only present for new uploads or replacements
  isPrimary: boolean
  ordinal: number
  altText?: string
  isExisting: boolean  // true = already saved on server, don't re-upload
  isDeleted?: boolean  // marked for deletion on save
}

// Helper: build PhotoEntry from a server-side GearPhoto
export function photoFromServer(p: { id: string; storageKey: string; url?: string; isPrimary: boolean; ordinal: number; altText?: string | null }): PhotoEntry {
  return {
    id: p.id,
    url: p.url ?? p.storageKey,  // use resolved URL if present, fall back to storageKey
    isPrimary: p.isPrimary,
    ordinal: p.ordinal,
    altText: p.altText ?? undefined,
    isExisting: true,
  }
}

type Props = {
  photos: PhotoEntry[]
  onChange: (photos: PhotoEntry[]) => void
  max?: number
  readOnly?: boolean
}

export function PhotoUpload({ photos, onChange, max = 20, readOnly = false }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  // Visible photos (not marked deleted)
  const visible = photos.filter(p => !p.isDeleted)

  const addFiles = useCallback((files: FileList | null) => {
    if (!files || readOnly) return
    const remaining = max - visible.length
    const toAdd = Array.from(files).slice(0, remaining)
    const newPhotos: PhotoEntry[] = toAdd.map((file, i) => ({
      id: `new_${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`,
      url: URL.createObjectURL(file),
      file,
      isPrimary: false,
      ordinal: visible.length + i,
      isExisting: false,
    }))
    const updated = [...photos, ...newPhotos]
    // Auto-set primary if none
    if (!updated.filter(p => !p.isDeleted).some(p => p.isPrimary) && updated.filter(p => !p.isDeleted).length > 0) {
      const first = updated.find(p => !p.isDeleted)
      if (first) first.isPrimary = true
    }
    onChange(updated)
  }, [photos, onChange, max, visible.length, readOnly])

  const remove = (id: string) => {
    if (readOnly) return
    const updated = photos.map(p => p.id === id ? { ...p, isDeleted: true, isPrimary: false } : p)
    // Reassign primary if we removed it
    const wasP = photos.find(p => p.id === id)?.isPrimary
    if (wasP) {
      const remaining = updated.filter(p => !p.isDeleted)
      if (remaining.length > 0) remaining[0].isPrimary = true
    }
    onChange(updated)
  }

  const setPrimary = (id: string) => {
    if (readOnly) return
    onChange(photos.map(p => ({ ...p, isPrimary: p.id === id })))
  }

  const moveLeft = (id: string) => {
    if (readOnly) return
    const idx = visible.findIndex(p => p.id === id)
    if (idx <= 0) return
    const updated = [...visible]
    ;[updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]]
    // Primary always follows position 0
    onChange(updated.map((p, i) => ({ ...p, ordinal: i, isPrimary: i === 0 })))
  }

  const moveRight = (id: string) => {
    if (readOnly) return
    const idx = visible.findIndex(p => p.id === id)
    if (idx >= visible.length - 1) return
    const updated = [...visible]
    ;[updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]]
    // Primary always follows position 0
    onChange(updated.map((p, i) => ({ ...p, ordinal: i, isPrimary: i === 0 })))
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    addFiles(e.dataTransfer.files)
  }

  return (
    <div className="space-y-3">
      {/* Upload zone — hidden in readOnly or when at max */}
      {!readOnly && visible.length < max && (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          className={[
            'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors',
            dragOver ? 'border-brand-orange bg-brand-orange/5' : 'border-zinc-700 hover:border-zinc-500',
          ].join(' ')}>
          <IconPhoto size={24} className="text-zinc-600 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">Drop photos here or click to browse</p>
          <p className="text-xs text-zinc-600 mt-1">
            {visible.length}/{max} photos · First selected is primary
          </p>
          <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={e => addFiles(e.target.files)} />
        </div>
      )}

      {/* Photo grid */}
      {visible.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {visible.map((photo, idx) => (
            <div key={photo.id}
              className="relative group aspect-[3/4] rounded-lg overflow-hidden bg-surface-raised border border-border-default">
              <img src={photo.url} alt={photo.altText ?? ''} className="w-full h-full object-cover" />

              {/* Primary badge */}
              {photo.isPrimary && (
                <div className="absolute top-1 left-1 bg-brand-orange text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1">
                  <IconStar size={9} fill="currentColor" /> Primary
                </div>
              )}

              {/* Ordinal */}
              <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] rounded px-1">
                {idx + 1}
              </div>

              {/* Hover controls */}
              {!readOnly && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                  {!photo.isPrimary && (
                    <button type="button" onClick={() => setPrimary(photo.id)}
                      title="Set as primary"
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-brand-orange/80 text-white text-[10px] hover:bg-brand-orange transition-colors">
                      <IconStar size={11} /> Primary
                    </button>
                  )}
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveLeft(photo.id)} disabled={idx === 0}
                      className="p-1.5 rounded bg-black/40 text-zinc-300 hover:text-white disabled:opacity-30 transition-colors">
                      <IconArrowLeft size={12} />
                    </button>
                    <button type="button" onClick={() => moveRight(photo.id)} disabled={idx === visible.length - 1}
                      className="p-1.5 rounded bg-black/40 text-zinc-300 hover:text-white disabled:opacity-30 transition-colors">
                      <IconArrowRight size={12} />
                    </button>
                    <button type="button" onClick={() => remove(photo.id)}
                      className="p-1.5 rounded bg-red-900/70 text-red-200 hover:bg-red-800 transition-colors">
                      <IconX size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
